import { MetricHelper } from 'classes/helpers/metric.helper';
import { BS_LIMITS } from 'enums/pitch-design';
import { MIN_SPEED_MPH } from 'enums/pitches';
import { t } from 'i18next';
import { IPitchDesignInputs } from 'interfaces/i-pitch-design';
import { IScalingConfiguration } from 'interfaces/i-pitch-scaling';
import { AimingHelper } from 'lib_ts/classes/aiming.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { getMagnitude, MPH_TO_FTPS } from 'lib_ts/classes/math.utilities';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { MS_LIMITS } from 'lib_ts/enums/machine.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import {
  IBallState,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { ISpin, ISpinExt, IVelocity } from 'lib_ts/interfaces/pitches/i-base';
import { clamp } from 'lodash';

export class PitchDesignHelper {
  static HB_TOOLTIP_TEXT = 'Negative values correspond to movement towards 1B';
  static VB_TOOLTIP_TEXT = 'This value does not include gravity';

  static validateBallSpeedMph(value: number | undefined): boolean {
    if (value === undefined) {
      return false;
    }

    return (
      clamp(value, BS_LIMITS.SPEED_MPH.MIN, BS_LIMITS.SPEED_MPH.MAX) === value
    );
  }

  static validateGyroAngle(value: number | undefined): boolean {
    if (value === undefined) {
      return false;
    }

    return (
      clamp(value, BS_LIMITS.GYRO_DEG.MIN, BS_LIMITS.GYRO_DEG.MAX) === value
    );
  }

  static validateSpinAxis(value: number | undefined): boolean {
    if (value === undefined) {
      return false;
    }

    return (
      clamp(value, BS_LIMITS.AXIS_DEG.MIN, BS_LIMITS.AXIS_DEG.MAX) === value
    );
  }

  static validateSpin(value: number | undefined): boolean {
    if (value === undefined) {
      return false;
    }

    return (
      clamp(value, BS_LIMITS.SPIN_RPM.MIN, BS_LIMITS.SPIN_RPM.MAX) === value
    );
  }

  static validateNetSpin(value: number | undefined): boolean {
    if (value === undefined) {
      return false;
    }

    return (
      clamp(value, BS_LIMITS.NET_SPIN_RPM.MIN, BS_LIMITS.NET_SPIN_RPM.MAX) ===
      value
    );
  }

  static validateBreak(value: number | undefined): boolean {
    if (value === undefined) {
      return false;
    }

    return (
      clamp(value, BS_LIMITS.BREAK_IN.MIN, BS_LIMITS.BREAK_IN.MAX) === value
    );
  }

  /** returns a list of error messages based on whether pitch design form values are valid */
  static getFormErrors = (
    payload: {
      speedMph: number;
      spins: ISpin;
      spinExt: ISpinExt;
      breaks: ITrajektRefBreak;
    },
    priority: BuildPriority,
    isMetric: boolean | undefined
  ): string[] => {
    const output: string[] = [];

    if (!this.validateBallSpeedMph(payload.speedMph)) {
      output.push(
        t('pd.msg-speed-between-x-and-y-units', {
          x: (isMetric
            ? MetricHelper.mphToKph(BS_LIMITS.SPEED_MPH.MIN)
            : BS_LIMITS.SPEED_MPH.MIN
          ).toFixed(0),
          y: (isMetric
            ? MetricHelper.mphToKph(BS_LIMITS.SPEED_MPH.MAX)
            : BS_LIMITS.SPEED_MPH.MAX
          ).toFixed(0),
          units: MetricHelper.getSpeedUnit(isMetric, true),
        })
      );
    }

    switch (priority) {
      case BuildPriority.Spins: {
        if (!this.validateNetSpin(payload.spinExt.wnet)) {
          output.push(
            t('pd.msg-spin-between-x-rpm', {
              x: BS_LIMITS.NET_SPIN_RPM.MAX,
            })
          );
        }

        if (!this.validateGyroAngle(payload.spinExt.gyro_angle)) {
          output.push(
            t('pd.msg-gyro-angle-between-x-y-degrees', {
              x: BS_LIMITS.GYRO_DEG.MIN,
              y: BS_LIMITS.GYRO_DEG.MAX,
            })
          );
        }

        if (!this.validateSpinAxis(payload.spinExt.waxis)) {
          output.push(
            t('pd.msg-spin-axis-between-x-y-degrees', {
              x: BS_LIMITS.AXIS_DEG.MIN,
              y: BS_LIMITS.AXIS_DEG.MAX,
            })
          );
        }
        break;
      }

      case BuildPriority.Breaks: {
        if (!this.validateSpin(payload.spins.wy)) {
          output.push(
            t('pd.msg-spin-between-x-rpm', {
              x: BS_LIMITS.SPIN_RPM.MAX,
            })
          );
        }

        if (!payload.breaks) {
          output.push('Breaks are not defined.');
          break;
        }

        const breaks = [
          { name: 'Horizontal break', value: payload.breaks.xInches },
          { name: 'Vertical break', value: payload.breaks.zInches },
        ];

        breaks.forEach((m) => {
          if (m.value > BS_LIMITS.BREAK_IN.MAX) {
            output.push(
              t(
                isMetric
                  ? 'pd.msg-break-over-x-cm'
                  : 'pd.msg-break-over-x-inches',
                {
                  break: m.name,
                  x: isMetric
                    ? MetricHelper.inToCm(BS_LIMITS.BREAK_IN.MAX)
                    : BS_LIMITS.BREAK_IN.MAX,
                }
              )
            );
          } else if (m.value < BS_LIMITS.BREAK_IN.MIN) {
            output.push(
              t(
                isMetric
                  ? 'pd.msg-break-under-x-cm'
                  : 'pd.msg-break-under-x-inches',
                {
                  break: m.name,
                  x: isMetric
                    ? MetricHelper.inToCm(BS_LIMITS.BREAK_IN.MIN)
                    : BS_LIMITS.BREAK_IN.MIN,
                }
              )
            );
          }
        });
        break;
      }

      case BuildPriority.Default:
      default: {
        if (!this.validateSpin(payload.spins.wx)) {
          output.push(
            t('pd.msg-spin-between-x-rpm', {
              x: BS_LIMITS.SPIN_RPM.MAX,
            })
          );
        }

        if (!this.validateSpin(payload.spins.wy)) {
          output.push(
            t('pd.msg-spin-between-x-rpm', {
              x: BS_LIMITS.SPIN_RPM.MAX,
            })
          );
        }

        if (!this.validateSpin(payload.spins.wz)) {
          output.push(
            t('pd.msg-spin-between-x-rpm', {
              x: BS_LIMITS.SPIN_RPM.MAX,
            })
          );
        }
        break;
      }
    }

    return output;
  };

  /** returns a list of error messages based on whether pitch upload CSV format is valid */
  static getBulkPitchUploadErrors = (
    payload: {
      bs: IBallState;
      breaks?: ITrajektRefBreak;
    },
    priority: BuildPriority
  ): string[] => {
    const output: string[] = [];

    if (!this.validateBallSpeedMph(payload.bs.vnet)) {
      output.push(
        t('pd.msg-speed-between-x-and-y-units', {
          x: BS_LIMITS.SPEED_MPH.MIN,
          y: BS_LIMITS.SPEED_MPH.MAX,
          units: 'MPH',
        })
      );
    }

    switch (priority) {
      case BuildPriority.Spins: {
        if (!this.validateNetSpin(payload.bs.wnet)) {
          output.push(
            t('pd.msg-spin-between-x-rpm', {
              x: BS_LIMITS.NET_SPIN_RPM.MAX,
            })
          );
        }
        break;
      }

      case BuildPriority.Breaks: {
        if (!this.validateSpin(payload.bs.wy)) {
          output.push(
            t('pd.msg-spin-between-x-rpm', {
              x: BS_LIMITS.SPIN_RPM.MAX,
            })
          );
        }

        if (!payload.breaks) {
          output.push('Breaks are not defined.');
          break;
        }

        const breaks = [
          { name: 'Horizontal break', value: payload.breaks.xInches },
          { name: 'Vertical break', value: payload.breaks.zInches },
        ];

        breaks.forEach((m) => {
          if (m.value > BS_LIMITS.BREAK_IN.MAX) {
            output.push(
              t('pd.msg-break-over-x-inches', {
                break: m.name,
                x: BS_LIMITS.BREAK_IN.MAX,
              })
            );
          } else if (m.value < BS_LIMITS.BREAK_IN.MIN) {
            output.push(
              t('pd.msg-break-under-x-inches', {
                break: m.name,
                x: BS_LIMITS.BREAK_IN.MIN,
              })
            );
          }
        });
        break;
      }

      case BuildPriority.Default:
      default: {
        break;
      }
    }

    return output;
  };

  static getDisplayBuildPriority = (v: BuildPriority): string => {
    switch (v) {
      case BuildPriority.Breaks: {
        return 'Breaks Priority';
      }

      case BuildPriority.Default:
      case BuildPriority.Spins:
      default: {
        return 'Spins Priority';
      }
    }
  };

  static getScaledChars = (
    pitch: {
      priority: BuildPriority;
      bs: IBallState;
      traj: ITrajectory; // will be used for plate
      breaks: ITrajektRefBreak | undefined;
    },
    scaling: IScalingConfiguration
  ): {
    bs: IBallState;
    traj: ITrajectory;
    breaks: ITrajektRefBreak | undefined;
  } => {
    const scaledBreaks =
      pitch.priority !== BuildPriority.Breaks
        ? // use original breaks (if any)
          pitch.breaks
        : // scale breaks by factors
          {
            xInches: scaling.breakX * (pitch.breaks?.xInches ?? 0),
            zInches: scaling.breakZ * (pitch.breaks?.zInches ?? 0),
          };

    const bsVelo: IVelocity = {
      vx: scaling.speed * pitch.bs.vx,
      // do not let the speed drop below 50mph
      vy: -Math.max(Math.abs(scaling.speed * pitch.bs.vy), MIN_SPEED_MPH),
      vz: scaling.speed * pitch.bs.vz,
    };

    const scaledBs: IBallState = {
      ...pitch.bs,
      ...bsVelo,
      vnet: BallHelper.getSpeed(bsVelo),
    };

    const getTraj = (): ITrajectory => {
      const plate = TrajHelper.getPlateLoc(pitch.traj);

      switch (pitch.priority) {
        case BuildPriority.Breaks: {
          // * should match the scaledBreaks case for break priority
          const safeBreaks: ITrajektRefBreak = {
            xInches: scaledBreaks?.xInches ?? 0,
            zInches: scaledBreaks?.zInches ?? 0,
          };

          return TrajHelper.getBreaksTraj({
            releasePos: {
              px: pitch.traj.px,
              py: pitch.traj.py,
              pz: pitch.traj.pz,
            },
            breaksFront: safeBreaks,
            plateMid: plate,
            vnetFTPS: scaledBs.vnet * MPH_TO_FTPS,
            wnetRPM: scaledBs.wnet,
          });
        }

        case BuildPriority.Spins:
        case BuildPriority.Default:
        default: {
          return TrajHelper.getSpinsTraj(scaledBs, plate);
        }
      }
    };

    return {
      bs: scaledBs,
      traj: getTraj(),
      breaks: scaledBreaks,
    };
  };

  static deriveBsAndTraj = (
    inputs: IPitchDesignInputs,
    priority: BuildPriority
  ): { bs: IBallState; traj: ITrajectory } => {
    // 0 speed will break everything b/c the ball will never arrive
    const safeSpeedMph = Math.max(inputs.speedMph, 1e-3);

    // ensure release X is within machine limits
    inputs.releasePos.px = clamp(
      inputs.releasePos.px,
      MS_LIMITS.POSITION.X.MIN + 1e-3,
      MS_LIMITS.POSITION.X.MAX - 1e-3
    );

    // ensure release Y is within machine limits
    inputs.releasePos.pz = clamp(
      inputs.releasePos.pz,
      MS_LIMITS.POSITION.Z.MIN + 1e-3,
      MS_LIMITS.POSITION.Z.MAX - 1e-3
    );

    switch (priority) {
      case BuildPriority.Breaks: {
        const traj = TrajHelper.getBreaksTraj({
          releasePos: {
            px: inputs.releasePos.px,
            py: inputs.releasePos.py,
            pz: inputs.releasePos.pz,
          },
          breaksFront: inputs.breaks,
          plateMid: inputs.plate,
          vnetFTPS: safeSpeedMph * MPH_TO_FTPS,
        });

        const bs = BallHelper.getBsFromTraj(
          traj,
          inputs.seams,
          inputs.spins.wy
        );
        return { bs, traj };
      }

      case BuildPriority.Spins:
      case BuildPriority.Default:
      default: {
        const quat = BallHelper.getQuatFromSeams({
          latitude_deg: inputs.seams.latitude_deg,
          longitude_deg: inputs.seams.longitude_deg,
          spin: inputs.spins,
        });

        const bs: IBallState = {
          px: inputs.releasePos.px,
          py: inputs.releasePos.py,
          pz: inputs.releasePos.pz,

          qw: quat.qw,
          qx: quat.qx,
          qy: quat.qy,
          qz: quat.qz,

          wx: inputs.spins.wx,
          wy: inputs.spins.wy,
          wz: inputs.spins.wz,
          wnet: getMagnitude([
            inputs.spins.wx,
            inputs.spins.wy,
            inputs.spins.wz,
          ]),

          vx: 0,
          vy: -safeSpeedMph,
          vz: 0,
          vnet: Math.abs(safeSpeedMph),
        };

        if ([bs.wx, bs.wy, bs.wz].every((m) => m === 0)) {
          // * IMPORTANT - avoid all 0's for spins
          bs.wx = 0.1;
        }

        const traj = TrajHelper.getSpinsTraj(bs, inputs.plate);

        const aimed = AimingHelper.aimBsTraj({
          // directly from input
          releasePos: inputs.releasePos,
          plate: inputs.plate,

          // derived above
          bs: bs,
          traj: traj,
        });

        return {
          bs: aimed.bs,
          traj: aimed.traj,
        };
      }
    }
  };
}
